import { inject, Injectable } from '@angular/core';
import { switchMap } from 'rxjs';
import dayjs from '../../core/dayjs-config';
import { AccountService } from '../../core/service/account.service';
import { RestClientWrapper } from '../../core/market/rest-client-factory';
import { WebsocketClientWrapper } from '../../core/market/websocket-client-factory';
import { BybitBalanceRestProvider, BybitBalanceWebsocketProvider } from './exchange/bybit-provider';
import { BinanceUsdmBalanceWebsocketProvider, BinanceUsdmBalanceRestProvider } from './exchange/binance-provider';
import { OkxBalanceRestProvider, OkxBalanceWebsocketProvider } from './exchange/okx-provider';

@Injectable({
  providedIn: 'root'
})
export class BalanceService {

  private account = inject(AccountService)

  private createRestProvider(rest: RestClientWrapper) {
    if (rest.isBybit) {
      return new BybitBalanceRestProvider(rest.bybit)
    }
    if (rest.isBinanceUsdm) {
      return new BinanceUsdmBalanceRestProvider(rest.binanceUsdm)
    }
    if (rest.isOkx) {
      return new OkxBalanceRestProvider(rest.okx)
    }
    throw new Error('RestClient type error')
  }

  private createWebsocketProvider(rest: RestClientWrapper, ws: WebsocketClientWrapper) {
    if (ws.isBybit) {
      return new BybitBalanceWebsocketProvider(ws.bybit)
    }
    if (ws.isBinance) {
      return new BinanceUsdmBalanceWebsocketProvider(rest.binanceUsdm, ws.binance)
    }
    if (ws.isOkx) {
      return new OkxBalanceWebsocketProvider(ws.okx)
    }
    throw new Error('WsClient type error')
  }

  requestBalance(rest: RestClientWrapper) {
    return this.createRestProvider(rest).requestBalance()
  }

  requestBalances(rest: RestClientWrapper) {
    return this.createRestProvider(rest).requestBalances()
  }

  requestPnl(rest: RestClientWrapper) {
    return this.account.getProfile().pipe(
      switchMap(profile => {
        const timezone = profile?.timezone || 'UTC'
        const startTime = dayjs().tz(timezone).startOf('day').valueOf()
        return this.createRestProvider(rest).requestPnl(startTime)
      })
    )
  }

  requestPnls(rest: RestClientWrapper) {
    return this.account.getProfile().pipe(
      switchMap(profile => {
        const timezone = profile?.timezone || 'UTC'
        const startTime = dayjs().tz(timezone).startOf('day').valueOf()
        return this.createRestProvider(rest).requestPnls(startTime)
      })
    )
  }

  realtimeBalance(rest: RestClientWrapper, ws: WebsocketClientWrapper) {
    return this.createWebsocketProvider(rest, ws).realtimeBalance()
  }

  realtimeBalances(rest: RestClientWrapper, ws: WebsocketClientWrapper) {
    return this.createWebsocketProvider(rest, ws).realtimeBalances()
  }

  realtimePnlChange(rest: RestClientWrapper, ws: WebsocketClientWrapper) {
    return this.createWebsocketProvider(rest, ws).realtimePnlChange()
  }

}
